import React from 'react';
import { makeStyles } from '@material-ui/core';
import { compose } from 'ramda';
import {
  Field,
  reduxForm,
  isValid,
  Form,
  InjectedFormProps,
  FieldArray,
  arrayPush
} from 'redux-form';
import Fields from 'components/Fields';
import { Button, FieldPercent, BoxWrapper } from 'components/Base';
import Store from 'store/store';
import { MetaType } from 'store/modules/Meta/types';
import AssetClasses from './AssetClasses/AssetClasses';
import { formatPercent } from 'utils';

const { connect, selectors } = Store;

const useStyles = makeStyles({
  paper: {
    minWidth: '28rem',
    backgroundColor: '#fff'
  },
  hidden: {
    visibility: 'hidden'
  },
  title: {
    marginBottom: '1rem'
  },
  btnBox: {
    marginTop: '2rem',
    marginBottom: '1rem',
    display: 'flex',
    justifyContent: 'space-between'
  },
  form: {
    padding: '0 1rem'
  },
  field: {
    margin: '0.5rem 0'
  },
  costs: {
    display: 'flex',
    justifyContent: 'space-between',
    '& > div': {
      width: '45%'
    }
  },
  assetClasses: {
    marginTop: '1rem'
  }
});

interface Props {
  assetClasses: Array<MetaType>;
  arrayPush: (form: string, fieldArray: string, value: any) => void;
  closeModalFunc: () => void;
  onSubmit: any;
}

const ManualFund = ({
  handleSubmit,
  submitting,
  assetClasses,
  arrayPush,
  closeModalFunc,
  onSubmit
}: InjectedFormProps<Props> & Props) => {
  const classes = useStyles();

  const addNewClass = () => {
    return arrayPush('manualFund', 'asset_classes', {
      percentage_value: formatPercent(0),
      asset_class: null
    });
  };

  const handleCustomSubmit = (event: any) => {
    event.preventDefault();
    event.stopPropagation();

    handleSubmit(formData => {
      onSubmit(formData);
    })();
  };

  return (
    <BoxWrapper
      title="Add new fund"
      className={classes.paper}
      tooltip="The risk rating of a non-retail fund can be determined by specifying the asset allocation."
      closeModal={closeModalFunc}
    >
      <Form onSubmit={handleCustomSubmit} className={classes.form}>
        <Field
          name="name"
          component={Fields.Text}
          validate={Fields.Validators.required}
          label="Fund name"
          className={classes.field}
        />
        <div className={classes.costs}>
          <FieldPercent
            name="ocf"
            component={Fields.Text}
            placeholder="0.00%"
            validate={[Fields.Validators.isPercentageValid]}
            label="OCF"
            className={classes.field}
          />
          <FieldPercent
            name="trading_costs"
            placeholder="0.00%"
            component={Fields.Text}
            validate={[Fields.Validators.isPercentageValid]}
            label="Transaction cost"
            className={classes.field}
          />
        </div>
        <div className={classes.assetClasses}>
          <FieldArray
            name="asset_classes"
            component={AssetClasses}
            assetClasses={assetClasses}
          />
        </div>

        <div className={classes.btnBox}>
          <Button color="primary" onClick={closeModalFunc}>
            Cancel
          </Button>
          <div>
            <Button style={{ margin: ' 0 1rem' }} onClick={addNewClass}>
              Add asset class
            </Button>
            <Button isLoading={submitting} type="submit">
              Add
            </Button>
          </div>
        </div>
      </Form>
    </BoxWrapper>
  );
};

export default compose(
  reduxForm({
    form: 'manualFund'
  }),
  connect(
    (state: any) => ({
      isValid: isValid('manualFund')(state),
      assetClasses: selectors.metaSelectors.assetClasses(state)
    }),
    {
      arrayPush: arrayPush
    }
  )
)(ManualFund);
